import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Mail, Package, Image, MessageSquare, BookOpen,
  FileText, Settings, LogOut, Plus, Trash2, Edit2, Save, X, Eye,
  CheckCircle, Clock, Zap, Globe, ShoppingCart, DollarSign, Users, Code, Lock, KeyRound, Shield, BarChart3,
  ChevronUp, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "contacts", label: "Leads Inbox", icon: Mail },
  { id: "chats", label: "Live Chats", icon: MessageSquare },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "purchases", label: "Purchases", icon: ShoppingCart },
  { id: "packages", label: "Packages", icon: Package },
  { id: "portfolio", label: "Portfolio", icon: Image },
  { id: "testimonials", label: "Testimonials", icon: MessageSquare },
  { id: "blog", label: "Blog Posts", icon: BookOpen },
  { id: "pages", label: "Page Editor", icon: FileText },
  { id: "services", label: "Service Pages", icon: Globe },
  { id: "tracking", label: "Tracking Codes", icon: Code },
  { id: "users", label: "User Management", icon: Users },
  { id: "whitelist", label: "IP Whitelist", icon: Shield },
  { id: "settings", label: "Settings", icon: Settings },
];

// ─── Overview ─────────────────────────────────────────────────────────────────
function Overview({ authHeaders }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/stats`, authHeaders()).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats ? [
    { label: "Total Leads", value: stats.contacts, sub: `${stats.new_contacts} new`, icon: Mail, color: "bg-[#FFD600]" },
    { label: "Purchases", value: stats.purchases || 0, sub: `${stats.new_purchases || 0} new`, icon: ShoppingCart, color: "bg-green-400" },
    { label: "Portfolio Items", value: stats.portfolio, sub: "Projects shown", icon: Image, color: "bg-black text-white" },
    { label: "Testimonials", value: stats.testimonials, sub: "Client reviews", icon: MessageSquare, color: "bg-white" },
  ] : [];

  return (
    <div data-testid="admin-overview">
      <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>Dashboard</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className={`${c.color} border-2 border-black rounded-2xl p-6`} style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }} data-testid={`stat-card-${i}`}>
            <c.icon size={24} className="mb-3" />
            <div className="text-4xl font-black mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{c.value}</div>
            <div className="font-bold text-sm">{c.label}</div>
            <div className="text-xs opacity-70 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>
      <div className="bg-[#F4F4F5] border-2 border-black rounded-2xl p-6">
        <h3 className="font-black text-lg mb-4">Quick Links</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["View Live Site", "Get Quote Link", "Call: 0466 476 264", "Email Support"].map((l, i) => (
            <div key={i} className="bg-white border-2 border-black rounded-xl p-3 text-center text-sm font-bold hover:bg-[#FFD600] transition-colors cursor-pointer">{l}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Contacts ─────────────────────────────────────────────────────────────────
function Contacts({ authHeaders }) {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); }, []);
  const load = () => axios.get(`${API}/admin/contacts`, authHeaders()).then(r => setContacts(r.data)).catch(() => {});

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/contacts/${id}?status=${status}`, {}, authHeaders());
    load(); toast.success("Status updated");
  };
  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await axios.delete(`${API}/admin/contacts/${id}`, authHeaders());
    setSelected(null); load(); toast.success("Deleted");
  };

  const statusColors = { new: "bg-[#FFD600] text-black", read: "bg-gray-100 text-gray-700", responded: "bg-green-100 text-green-800" };

  return (
    <div data-testid="admin-contacts">
      <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>Leads Inbox ({contacts.length})</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-3">
          {contacts.length === 0 ? <p className="text-gray-500 text-sm">No leads yet</p> : contacts.map(c => (
            <div
              key={c.id}
              onClick={() => { setSelected(c); updateStatus(c.id, "read"); }}
              className={`border-2 border-black rounded-xl p-4 cursor-pointer transition-all hover:-translate-y-0.5 ${selected?.id === c.id ? "bg-[#FFD600]" : "bg-white"}`}
              style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}
              data-testid={`contact-item-${c.id}`}
            >
              <div className="flex items-start justify-between mb-1">
                <p className="font-black text-sm truncate">{c.name}</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border border-black ${statusColors[c.status] || "bg-gray-100"}`}>{c.status}</span>
              </div>
              <p className="text-xs text-gray-600 truncate">{c.email}</p>
              {c.business_name && <p className="text-xs font-semibold mt-1">{c.business_name}</p>}
              {c.package_interest && <p className="text-xs text-[#0055FF] font-semibold">{c.package_interest}</p>}
              <p className="text-xs text-gray-400 mt-1">{new Date(c.created_at).toLocaleDateString("en-AU")}</p>
            </div>
          ))}
        </div>
        <div className="md:col-span-2">
          {selected ? (
            <div className="bg-white border-2 border-black rounded-2xl p-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{selected.name}</h3>
                  {selected.business_name && <p className="font-semibold text-gray-600">{selected.business_name}</p>}
                </div>
                <button onClick={() => del(selected.id)} className="p-2 border-2 border-black rounded-xl hover:bg-red-50 transition-colors" data-testid="delete-contact-btn"><Trash2 size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div><p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-1">Email</p><a href={`mailto:${selected.email}`} className="font-semibold text-[#0055FF]">{selected.email}</a></div>
                {selected.phone && <div><p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-1">Phone</p><a href={`tel:${selected.phone}`} className="font-semibold">{selected.phone}</a></div>}
                {selected.package_interest && <div><p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-1">Package</p><p className="font-semibold">{selected.package_interest}</p></div>}
                <div><p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-1">Date</p><p className="font-semibold">{new Date(selected.created_at).toLocaleString("en-AU")}</p></div>
              </div>
              <div className="bg-[#F4F4F5] border-2 border-black rounded-xl p-4 mb-6">
                <p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-2">Message</p>
                <p className="text-sm leading-relaxed">{selected.message}</p>
              </div>
              <div className="flex gap-3 flex-wrap">
                {["new", "read", "responded"].map(s => (
                  <button key={s} onClick={() => updateStatus(selected.id, s)}
                    className={`px-4 py-2 border-2 border-black rounded-xl text-sm font-bold capitalize transition-colors ${selected.status === s ? "bg-[#FFD600]" : "hover:bg-[#FFD600]"}`}
                    data-testid={`status-${s}`}>{s}</button>
                ))}
                <a href={`mailto:${selected.email}?subject=Your Website Quote — No Fuss Websites`} className="ml-auto px-4 py-2 bg-black text-white border-2 border-black rounded-xl text-sm font-bold hover:bg-[#0055FF] transition-colors">
                  Reply via Email
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-[#F4F4F5] border-2 border-black rounded-2xl p-12 flex items-center justify-center text-center">
              <div>
                <Mail size={48} className="mx-auto mb-4 opacity-30" />
                <p className="font-bold text-gray-500">Select a lead to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Live Chats ───────────────────────────────────────────────────────────────
function LiveChats({ authHeaders }) {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/chat-sessions`, authHeaders()).then(r => setSessions(r.data)).catch(() => {});
  }, []);

  const viewSession = async (sid) => {
    try {
      const r = await axios.get(`${API}/admin/chat-sessions/${sid}`, authHeaders());
      setDetail(r.data);
      setSelected(sid);
    } catch { toast.error("Failed to load chat"); }
  };

  return (
    <div data-testid="admin-chats">
      <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Live Chats</h2>
      <p className="text-gray-600 mb-6">All chatbot conversations with visitors. {sessions.length} total sessions.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sessions list */}
        <div className="lg:col-span-1 bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <div className="p-4 border-b-2 border-black bg-gray-50">
            <h3 className="font-black text-sm uppercase tracking-wider">Sessions ({sessions.length})</h3>
          </div>
          <div className="max-h-[600px] overflow-y-auto divide-y divide-gray-200">
            {sessions.length === 0 ? (
              <p className="p-6 text-center text-gray-400 text-sm">No chat sessions yet</p>
            ) : sessions.map(s => (
              <button key={s.session_id} onClick={() => viewSession(s.session_id)}
                className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${selected === s.session_id ? "bg-[#FFD600]/20 border-l-4 border-[#FFD600]" : ""}`}
                data-testid={`chat-session-${s.session_id}`}>
                <div className="flex justify-between items-start mb-1">
                  <p className="font-bold text-sm">{s.user_name}</p>
                  <span className="text-xs bg-black text-white px-2 py-0.5 rounded-full">{s.message_count} msgs</span>
                </div>
                {s.user_email && <p className="text-xs text-gray-500">{s.user_email}</p>}
                <p className="text-xs text-gray-400 mt-1 truncate">{s.preview || "No messages"}</p>
                <p className="text-xs text-gray-400 mt-1">{new Date(s.last_message).toLocaleString()}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Chat detail */}
        <div className="lg:col-span-2 bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          {!detail ? (
            <div className="p-12 text-center text-gray-400">
              <MessageSquare size={48} className="mx-auto mb-4 opacity-20" />
              <p className="font-bold">Select a chat session to view</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b-2 border-black bg-gray-50 flex items-center justify-between">
                <div>
                  <h3 className="font-black text-sm">{detail.meta?.name || "Anonymous"}</h3>
                  {detail.meta?.email && <p className="text-xs text-gray-500">{detail.meta.email}</p>}
                </div>
                <span className="text-xs text-gray-400">{detail.messages?.length || 0} messages</span>
              </div>
              <div className="p-4 space-y-3 max-h-[550px] overflow-y-auto">
                {(detail.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                      m.role === "user" ? "bg-black text-white rounded-br-sm" : "bg-gray-100 border border-gray-200 rounded-bl-sm"
                    }`}>
                      <p>{m.content}</p>
                      <p className={`text-xs mt-1 ${m.role === "user" ? "text-gray-400" : "text-gray-400"}`}>
                        {new Date(m.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Analytics ────────────────────────────────────────────────────────────────
function Analytics({ authHeaders }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/analytics`, authHeaders()).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="animate-pulse p-8 text-center">Loading analytics...</div>;

  const maxViews = Math.max(...(data.top_pages || []).map(p => p.views), 1);

  const FUNNEL_LABELS = {
    "select_package": "1. Select Package",
    "add_extras": "2. Add Extras",
    "how_it_works": "3. How It Works",
    "your_details": "4. Your Details",
    "review_and_pay": "5. Review & Pay",
  };

  return (
    <div data-testid="admin-analytics">
      <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Analytics</h2>
      <p className="text-gray-600 mb-6">Website traffic and purchase funnel insights.</p>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Views (7d)", value: data.totals.views_7d },
          { label: "Views (30d)", value: data.totals.views_30d },
          { label: "Chat Leads", value: data.totals.chats },
          { label: "Contacts (30d)", value: data.totals.contacts_30d },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white border-2 border-black rounded-2xl p-5 text-center" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
            <p className="text-3xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Pages */}
        <div className="bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <div className="p-4 border-b-2 border-black bg-gray-50">
            <h3 className="font-black text-sm uppercase tracking-wider">Top Pages (30 days)</h3>
          </div>
          <div className="p-4 space-y-3">
            {(data.top_pages || []).slice(0, 10).map((p, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-mono font-bold truncate max-w-[200px]">{p.path || "/"}</span>
                  <span className="text-gray-600 shrink-0">{p.views} views / {p.unique_visitors} unique</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 border border-gray-200">
                  <div className="bg-[#FFD600] h-2.5 rounded-full border-r border-black" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                </div>
              </div>
            ))}
            {(data.top_pages || []).length === 0 && <p className="text-center text-gray-400 py-4 text-sm">No data yet</p>}
          </div>
        </div>

        {/* Purchase Funnel */}
        <div className="bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <div className="p-4 border-b-2 border-black bg-gray-50">
            <h3 className="font-black text-sm uppercase tracking-wider">Purchase Funnel (30 days)</h3>
          </div>
          <div className="p-4 space-y-3">
            {Object.entries(FUNNEL_LABELS).map(([key, label]) => {
              const step = (data.step_events || []).find(e => e._id === key);
              const count = step ? step.count : 0;
              const maxStep = Math.max(...(data.step_events || []).map(e => e.count), 1);
              return (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-bold">{label}</span>
                    <span className="text-gray-600">{count} users</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 border border-gray-200">
                    <div className={`h-3 rounded-full ${count > 0 ? "bg-black" : "bg-gray-200"}`} style={{ width: `${count > 0 ? (count / maxStep) * 100 : 0}%` }} />
                  </div>
                </div>
              );
            })}
            {(data.step_events || []).length === 0 && <p className="text-center text-gray-400 py-4 text-sm">No purchase data yet</p>}
          </div>
        </div>
      </div>

      {/* Daily Views */}
      <div className="bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
        <div className="p-4 border-b-2 border-black bg-gray-50">
          <h3 className="font-black text-sm uppercase tracking-wider">Daily Views (Last 7 Days)</h3>
        </div>
        <div className="p-4">
          <div className="flex items-end gap-2 h-40">
            {(data.daily_views || []).map((d, i) => {
              const maxDay = Math.max(...(data.daily_views || []).map(x => x.views), 1);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-bold">{d.views}</span>
                  <div className="w-full bg-[#FFD600] border border-black rounded-t" style={{ height: `${(d.views / maxDay) * 120}px`, minHeight: "4px" }} />
                  <span className="text-xs text-gray-500">{d._id?.slice(5)}</span>
                </div>
              );
            })}
          </div>
          {(data.daily_views || []).length === 0 && <p className="text-center text-gray-400 py-8 text-sm">No data yet — views will appear after visitors browse the site</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Packages ─────────────────────────────────────────────────────────────────
function Packages({ authHeaders }) {
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => { load(); }, []);
  const load = () => axios.get(`${API}/packages`).then(r => setPackages(r.data)).catch(() => {});

  const save = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API}/admin/packages/${editing.id}`, editing, authHeaders());
      toast.success("Package updated!"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const updateField = (field, value) => setEditing({ ...editing, [field]: value });
  const updateFeatures = (val) => setEditing({ ...editing, features: val.split("\n").filter(f => f.trim()) });

  return (
    <div data-testid="admin-packages">
      <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>Packages Editor</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {packages.map(pkg => (
          <div key={pkg.id} className={`border-2 border-black rounded-2xl p-6 ${editing?.id === pkg.id ? "ring-2 ring-[#FFD600]" : ""}`} style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
            {editing?.id === pkg.id ? (
              <div className="space-y-3">
                <input value={editing.name} onChange={e => updateField("name", e.target.value)} className="w-full border-2 border-black rounded-xl px-3 py-2 font-black text-lg" />
                <input value={editing.tagline} onChange={e => updateField("tagline", e.target.value)} className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm" />
                <input type="number" value={editing.price} onChange={e => updateField("price", parseFloat(e.target.value))} className="w-full border-2 border-black rounded-xl px-3 py-2 font-black text-2xl" />
                <div>
                  <label className="text-xs font-black uppercase tracking-wider mb-1 block">Original Price (leave 0 or empty for no sale)</label>
                  <input type="number" value={editing.original_price || ""} onChange={e => updateField("original_price", e.target.value ? parseFloat(e.target.value) : null)} placeholder="e.g. 699" className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm" />
                  {editing.original_price && editing.original_price > editing.price && (
                    <p className="text-xs text-red-500 font-bold mt-1">Sale: {Math.round(((editing.original_price - editing.price) / editing.original_price) * 100)}% OFF</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id={`pop-${pkg.id}`} checked={editing.is_popular} onChange={e => updateField("is_popular", e.target.checked)} className="w-4 h-4" />
                  <label htmlFor={`pop-${pkg.id}`} className="text-sm font-bold">Mark as Popular</label>
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-wider mb-1">Features (one per line)</p>
                  <textarea value={(editing.features || []).join("\n")} onChange={e => updateFeatures(e.target.value)} rows={10} className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm resize-none" />
                </div>
                <div className="flex gap-2">
                  <button onClick={save} className="flex-1 bg-[#FFD600] border-2 border-black rounded-xl py-2 font-black text-sm flex items-center justify-center gap-1" data-testid={`save-package-${pkg.id}`}><Save size={14} /> Save</button>
                  <button onClick={() => setEditing(null)} className="flex-1 bg-white border-2 border-black rounded-xl py-2 font-bold text-sm flex items-center justify-center gap-1"><X size={14} /> Cancel</button>
                </div>
              </div>
            ) : (
              <>
                {pkg.is_popular && <span className="bg-[#FFD600] border border-black text-xs font-black px-2 py-0.5 rounded-full mb-3 inline-block">Most Popular</span>}
                <h3 className="text-2xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{pkg.name}</h3>
                <div className="flex items-center gap-2 my-2">
                  {pkg.original_price && pkg.original_price > pkg.price && (
                    <span className="text-lg line-through text-gray-400">${pkg.original_price}</span>
                  )}
                  <p className="text-3xl font-black">${pkg.price}</p>
                  {pkg.original_price && pkg.original_price > pkg.price && (
                    <span className="bg-red-500 text-white text-xs font-black px-2 py-0.5 rounded-full">
                      {Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100)}% OFF
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">{pkg.tagline}</p>
                <p className="text-xs text-gray-400 mb-4">{pkg.features?.length} features listed</p>
                <button onClick={() => setEditing({ ...pkg })} className="w-full bg-black text-white border-2 border-black rounded-xl py-2 font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#0055FF] transition-colors" data-testid={`edit-package-${pkg.id}`}><Edit2 size={14} /> Edit Package</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Generic CRUD Section ─────────────────────────────────────────────────────
function CRUDSection({ title, endpoint, fields, authHeaders, testId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { load(); }, []);
  const load = () => axios.get(endpoint, authHeaders()).then(r => setItems(r.data)).catch(() => {});

  const save = async () => {
    try {
      if (editing) {
        await axios.put(`${endpoint}/${editing.id}`, { ...editing, ...form }, authHeaders());
        toast.success("Updated!");
      } else {
        const data = {};
        fields.forEach(f => { data[f.key] = form[f.key] || ""; });
        await axios.post(endpoint, data, authHeaders());
        toast.success("Created!");
      }
      setForm({}); setEditing(null); setShowForm(false); load();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || "Unknown error")); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await axios.delete(`${endpoint}/${id}`, authHeaders());
    load(); toast.success("Deleted");
  };

  const startEdit = (item) => {
    setEditing(item);
    const f = {};
    fields.forEach(field => { f[field.key] = item[field.key] || ""; });
    setForm(f);
    setShowForm(true);
  };

  const moveItem = async (index, direction) => {
    const newItems = [...items];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newItems.length) return;
    [newItems[index], newItems[swapIdx]] = [newItems[swapIdx], newItems[index]];
    setItems(newItems);
    try {
      await axios.post(`${endpoint}/reorder`, { ids: newItems.map(i => i.id) }, authHeaders());
    } catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>
        <button onClick={() => { setEditing(null); setForm({}); setShowForm(!showForm); }}
          className="btn-primary flex items-center gap-2" data-testid={`add-${testId}`}>
          <Plus size={16} /> Add New
        </button>
      </div>

      {showForm && (
        <div className="bg-white border-2 border-black rounded-2xl p-8 mb-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <h3 className="font-black text-xl mb-6">{editing ? "Edit" : "Add New"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {fields.map(f => (
              <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    rows={f.rows || 4} placeholder={f.placeholder}
                    className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm resize-y font-mono" />
                ) : f.type === "checkbox" ? (
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={!!form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm font-semibold">{f.label}</span>
                  </div>
                ) : (
                  <input type={f.type || "text"} value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm" />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={save} className="btn-primary flex items-center gap-2" data-testid="save-item-btn"><Save size={14} /> Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); setForm({}); }} className="btn-secondary flex items-center gap-2"><X size={14} /> Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && <p className="text-gray-500 text-sm py-8 text-center">No items yet. Click "Add New" to get started.</p>}
        {items.map((item, i) => (
          <div key={item.id || i} className="bg-white border-2 border-black rounded-xl p-5 flex items-center gap-4" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
            {/* Reorder buttons */}
            <div className="flex flex-col gap-0.5 shrink-0">
              <button onClick={() => moveItem(i, -1)} disabled={i === 0}
                className="p-0.5 rounded hover:bg-[#FFD600] disabled:opacity-20 transition-colors" data-testid={`move-up-${i}`}>
                <ChevronUp size={14} />
              </button>
              <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1}
                className="p-0.5 rounded hover:bg-[#FFD600] disabled:opacity-20 transition-colors" data-testid={`move-down-${i}`}>
                <ChevronDown size={14} />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black truncate">{item.title || item.client_name || item.name || item[fields[0]?.key]}</p>
              {item.category && <span className="text-xs font-bold bg-[#FFD600] border border-black px-2 py-0.5 rounded-full">{item.category}</span>}
              {item.is_published !== undefined && (
                <span className={`text-xs font-bold border px-2 py-0.5 rounded-full ml-1 ${item.is_published ? "bg-green-100 border-green-600 text-green-800" : "bg-gray-100 border-gray-400"}`}>
                  {item.is_published ? "Published" : "Draft"}
                </span>
              )}
              {item.price !== undefined && <span className="text-xs font-bold ml-2">${item.price}</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(item)} className="p-2 border-2 border-black rounded-lg hover:bg-[#FFD600] transition-colors" data-testid={`edit-item-${i}`}><Edit2 size={14} /></button>
              <button onClick={() => del(item.id)} className="p-2 border-2 border-black rounded-lg hover:bg-red-100 transition-colors" data-testid={`delete-item-${i}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Purchases ────────────────────────────────────────────────────────────────
function Purchases({ authHeaders }) {
  const [purchases, setPurchases] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); }, []);
  const load = () => axios.get(`${API}/admin/purchases`, authHeaders()).then(r => setPurchases(r.data)).catch(() => {});

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/admin/purchases/${id}?status=${status}`, {}, authHeaders());
    load(); toast.success("Status updated");
  };

  const statusColors = { new: "bg-[#FFD600] text-black", in_progress: "bg-blue-100 text-blue-800", completed: "bg-green-100 text-green-800" };

  return (
    <div data-testid="admin-purchases">
      <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>Purchases ({purchases.length})</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-3">
          {purchases.length === 0 ? <p className="text-gray-500 text-sm py-8 text-center">No purchases yet</p> : purchases.map(p => (
            <div key={p.id} onClick={() => setSelected(p)}
              className={`border-2 border-black rounded-xl p-4 cursor-pointer transition-all hover:-translate-y-0.5 ${selected?.id === p.id ? "bg-[#FFD600]" : "bg-white"}`}
              style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }} data-testid={`purchase-item-${p.id}`}>
              <div className="flex items-start justify-between mb-1">
                <p className="font-black text-sm truncate">{p.customer_name}</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border border-black ${statusColors[p.status] || "bg-gray-100"}`}>{p.status}</span>
              </div>
              <p className="text-xs font-bold text-[#0055FF]">{p.package_name}</p>
              <p className="text-sm font-black">${p.total_amount?.toLocaleString()}</p>
              <p className="text-xs font-black text-gray-500">{p.ticket_number}</p>
              <p className="text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString("en-AU")}</p>
            </div>
          ))}
        </div>
        <div className="md:col-span-2">
          {selected ? (
            <div className="bg-white border-2 border-black rounded-2xl p-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{selected.customer_name}</h3>
                  {selected.business_name && <p className="font-semibold text-gray-600">{selected.business_name}</p>}
                  <p className="text-sm font-black text-[#0055FF]">{selected.ticket_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black">${selected.total_amount?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">AUD</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div><p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-1">Email</p><a href={`mailto:${selected.customer_email}`} className="font-semibold text-[#0055FF]">{selected.customer_email}</a></div>
                {selected.customer_phone && <div><p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-1">Phone</p><a href={`tel:${selected.customer_phone}`} className="font-semibold">{selected.customer_phone}</a></div>}
                <div><p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-1">Package</p><p className="font-semibold">{selected.package_name}</p></div>
                <div><p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-1">Date</p><p className="font-semibold">{new Date(selected.created_at).toLocaleString("en-AU")}</p></div>
              </div>
              {selected.addons?.length > 0 && (
                <div className="bg-[#F4F4F5] border-2 border-black rounded-xl p-4 mb-6">
                  <p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-2">Add-ons</p>
                  {selected.addons.map((a, i) => <p key={i} className="text-sm font-semibold">{a.name} — ${a.price}</p>)}
                </div>
              )}
              {selected.notes && (
                <div className="bg-[#F4F4F5] border-2 border-black rounded-xl p-4 mb-6">
                  <p className="font-black uppercase tracking-wider text-xs text-gray-500 mb-2">Notes</p>
                  <p className="text-sm">{selected.notes}</p>
                </div>
              )}
              <div className="flex gap-3 flex-wrap">
                {["new", "in_progress", "completed"].map(s => (
                  <button key={s} onClick={() => updateStatus(selected.id, s)}
                    className={`px-4 py-2 border-2 border-black rounded-xl text-sm font-bold capitalize transition-colors ${selected.status === s ? "bg-[#FFD600]" : "hover:bg-[#FFD600]"}`}
                    data-testid={`purchase-status-${s}`}>{s.replace("_", " ")}</button>
                ))}
                <a href={`mailto:${selected.customer_email}?subject=Your Website is Ready — ${selected.ticket_number}`}
                  className="ml-auto px-4 py-2 bg-black text-white border-2 border-black rounded-xl text-sm font-bold hover:bg-[#0055FF] transition-colors">
                  Email Customer
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-[#F4F4F5] border-2 border-black rounded-2xl p-12 flex items-center justify-center text-center">
              <div><ShoppingCart size={48} className="mx-auto mb-4 opacity-30" /><p className="font-bold text-gray-500">Select a purchase to view details</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tracking Codes ───────────────────────────────────────────────────────────
function TrackingCodes({ authHeaders }) {
  const [codes, setCodes] = useState({
    gtm_code: "", facebook_pixel_code: "", google_analytics_code: "",
    google_webmaster_code: "", custom_head_scripts: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, authHeaders()).then(r => {
      setCodes(c => ({
        gtm_code: r.data.gtm_code || "",
        facebook_pixel_code: r.data.facebook_pixel_code || "",
        google_analytics_code: r.data.google_analytics_code || "",
        google_webmaster_code: r.data.google_webmaster_code || "",
        custom_head_scripts: r.data.custom_head_scripts || "",
      }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, codes, authHeaders());
      toast.success("Tracking codes saved! Changes are live immediately.");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const fields = [
    { key: "gtm_code", label: "Google Tag Manager", placeholder: "<!-- Google Tag Manager -->\n<script>(function(w,d,s,l,i){w[l]=w[l]||[];...})(window,document,'script','dataLayer','GTM-XXXXXXX');</script>\n<!-- End Google Tag Manager -->", help: "Paste the full GTM <script> tag from tagmanager.google.com → Admin → Install" },
    { key: "google_analytics_code", label: "Google Analytics (GA4)", placeholder: "<!-- Google tag (gtag.js) -->\n<script async src=\"https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXX\"></script>\n<script>window.dataLayer=window.dataLayer||[];...</script>", help: "Paste the full GA4 tracking code from analytics.google.com → Admin → Data Streams" },
    { key: "google_webmaster_code", label: "Google Search Console / Webmaster Tools", placeholder: "<meta name=\"google-site-verification\" content=\"your-verification-code\" />", help: "Paste the HTML verification tag from search.google.com/search-console → Settings → Ownership verification" },
    { key: "facebook_pixel_code", label: "Facebook / Meta Pixel", placeholder: "<!-- Meta Pixel Code -->\n<script>!function(f,b,e,v,n,t,s){...}('your-pixel-id');</script>\n<!-- End Meta Pixel Code -->", help: "Paste the full Pixel code from business.facebook.com → Events Manager → Settings" },
    { key: "custom_head_scripts", label: "Custom Head Scripts", placeholder: "<!-- Any other scripts, meta tags, or verification codes -->", help: "Any additional code to inject into the <head> of every page (e.g., Hotjar, HubSpot, schema markup)" },
  ];

  return (
    <div data-testid="admin-tracking">
      <h2 className="text-3xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Tracking Codes</h2>
      <p className="text-gray-600 mb-8">Paste your full tracking and verification codes below. They will be injected into the &lt;head&gt; of every page and are publicly visible to crawlers and verification tools.</p>

      <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-4 mb-8">
        <p className="font-black text-sm">These codes go live immediately after saving. Paste the full &lt;script&gt; or &lt;meta&gt; tags exactly as provided by each service.</p>
      </div>

      <div className="space-y-6">
        {fields.map(f => (
          <div key={f.key} className="bg-white border-2 border-black rounded-2xl p-6" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
            <label className="text-sm font-black uppercase tracking-wider mb-2 block">{f.label}</label>
            <textarea
              value={codes[f.key] || ""}
              onChange={e => setCodes({ ...codes, [f.key]: e.target.value })}
              rows={5}
              placeholder={f.placeholder}
              className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#FFD600]"
              data-testid={`tracking-${f.key}`}
            />
            <p className="text-xs text-gray-500 mt-1.5">{f.help}</p>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-primary mt-8 flex items-center gap-2" data-testid="save-tracking-btn">
        <Save size={14} /> {saving ? "Saving..." : "Save Tracking Codes"}
      </button>
    </div>
  );
}

// ─── User Management ──────────────────────────────────────────────────────────
function UserManagement({ authHeaders }) {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [changingPassword, setChangingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => { load(); }, []);
  const load = () => axios.get(`${API}/admin/users`, authHeaders()).then(r => setUsers(r.data)).catch(() => {});

  const createUser = async () => {
    if (!form.email || !form.password) { toast.error("Email and password are required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await axios.post(`${API}/admin/users`, form, authHeaders());
      toast.success("User created!");
      setForm({ name: "", email: "", password: "" });
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create user"); }
  };

  const changePassword = async (userId) => {
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await axios.put(`${API}/admin/users/${userId}/password`, { password: newPassword }, authHeaders());
      toast.success("Password changed!");
      setChangingPassword(null);
      setNewPassword("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change password"); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, authHeaders());
      toast.success("User deleted");
      load();
    } catch { toast.error("Failed to delete user"); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>User Management</h2>
          <p className="text-gray-600 mt-1 text-sm">Manage staff accounts. Users log in with their email and password.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2" data-testid="add-user-btn">
          <Plus size={16} /> Add User
        </button>
      </div>

      {showForm && (
        <div className="bg-white border-2 border-black rounded-2xl p-8 mb-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <h3 className="font-black text-xl mb-6">Create New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Full Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith"
                className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm" data-testid="new-user-name" />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Email *</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@nofusswebsites.com.au"
                className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm" data-testid="new-user-email" />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Password *</label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters"
                className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm" data-testid="new-user-password" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={createUser} className="btn-primary flex items-center gap-2" data-testid="create-user-btn"><Save size={14} /> Create User</button>
            <button onClick={() => { setShowForm(false); setForm({ name: "", email: "", password: "" }); }} className="btn-secondary flex items-center gap-2"><X size={14} /> Cancel</button>
          </div>
        </div>
      )}

      {/* Owner account info */}
      <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-5 mb-6 flex items-center gap-4" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
        <div className="w-12 h-12 bg-black text-[#FFD600] border-2 border-black rounded-xl flex items-center justify-center shrink-0">
          <KeyRound size={20} />
        </div>
        <div className="flex-1">
          <p className="font-black">Owner Account</p>
          <p className="text-sm">Logs in with master password only (no email needed). Change this in Settings → Admin Password.</p>
        </div>
        <span className="text-xs font-bold bg-black text-[#FFD600] px-3 py-1 rounded-full">Active</span>
      </div>

      {/* User list */}
      <div className="space-y-4">
        {users.length === 0 && (
          <div className="bg-[#F4F4F5] border-2 border-black rounded-2xl p-12 text-center">
            <Users size={48} className="mx-auto mb-4 opacity-30" />
            <p className="font-bold text-gray-500">No staff accounts yet. Click "Add User" to create one.</p>
          </div>
        )}
        {users.map(u => (
          <div key={u.id} className="bg-white border-2 border-black rounded-2xl p-5" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }} data-testid={`user-${u.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#F4F4F5] border-2 border-black rounded-xl flex items-center justify-center shrink-0 font-black text-lg">
                {(u.name || u.email)[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-black truncate">{u.name || "No name"}</p>
                <p className="text-sm text-gray-600 truncate">{u.email}</p>
                <p className="text-xs text-gray-400">Added {new Date(u.created_at).toLocaleDateString("en-AU")}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => { setChangingPassword(changingPassword === u.id ? null : u.id); setNewPassword(""); }}
                  className={`p-2 border-2 border-black rounded-lg transition-colors ${changingPassword === u.id ? "bg-[#FFD600]" : "hover:bg-[#FFD600]"}`}
                  title="Change password" data-testid={`change-pw-${u.id}`}>
                  <Lock size={14} />
                </button>
                <button onClick={() => deleteUser(u.id, u.email)}
                  className="p-2 border-2 border-black rounded-lg hover:bg-red-100 transition-colors"
                  title="Delete user" data-testid={`delete-user-${u.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {changingPassword === u.id && (
              <div className="mt-4 pt-4 border-t-2 border-black flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-xs font-black uppercase tracking-wider mb-1 block">New Password</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters"
                    className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm" data-testid={`new-pw-input-${u.id}`} />
                </div>
                <button onClick={() => changePassword(u.id)} className="btn-primary flex items-center gap-2 shrink-0" data-testid={`save-pw-${u.id}`}>
                  <Save size={14} /> Update
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── IP Whitelist ─────────────────────────────────────────────────────────────
function IPWhitelist({ authHeaders }) {
  const [entries, setEntries] = useState([]);
  const [newIp, setNewIp] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/whitelist`, authHeaders()).then(r => setEntries(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const addIp = async () => {
    if (!newIp.trim()) { toast.error("Enter an IP address"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/whitelist`, { ip: newIp.trim(), label: newLabel.trim() }, authHeaders());
      toast.success(`IP ${newIp} whitelisted`);
      setNewIp(""); setNewLabel("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add IP"); }
    finally { setAdding(false); }
  };

  const removeIp = async (entry) => {
    if (!window.confirm(`Remove ${entry.ip} (${entry.label || "no label"}) from whitelist?`)) return;
    try {
      await axios.delete(`${API}/admin/whitelist/${entry.id}`, authHeaders());
      toast.success("IP removed");
      load();
    } catch { toast.error("Failed to remove IP"); }
  };

  return (
    <div data-testid="admin-whitelist">
      <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>IP Whitelist</h2>
      <p className="text-gray-600 mb-6">Only whitelisted IPs can access the admin panel. If the list is empty, all IPs are allowed.</p>

      <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-4 mb-6">
        <p className="font-black text-sm">Be careful: removing all IPs or removing your own IP will lock you out of the admin panel. If that happens, the whitelist can be cleared from the database directly.</p>
      </div>

      {/* Add new IP */}
      <div className="bg-white border-2 border-black rounded-2xl p-6 mb-6" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
        <h3 className="font-black text-lg mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Add IP Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">IP Address</label>
            <input value={newIp} onChange={e => setNewIp(e.target.value)} placeholder="e.g. 125.63.8.127"
              className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm font-mono" data-testid="whitelist-ip-input" />
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">Label (optional)</label>
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Office, Home"
              className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm" data-testid="whitelist-label-input" />
          </div>
          <div className="flex items-end">
            <button onClick={addIp} disabled={adding} className="btn-primary flex items-center gap-2 w-full justify-center" data-testid="whitelist-add-btn">
              <Plus size={14} /> {adding ? "Adding..." : "Add IP"}
            </button>
          </div>
        </div>
      </div>

      {/* Existing IPs */}
      <div className="bg-white border-2 border-black rounded-2xl overflow-hidden" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
        <div className="p-4 border-b-2 border-black bg-gray-50">
          <h3 className="font-black text-sm uppercase tracking-wider">Whitelisted IPs ({entries.length})</h3>
        </div>
        {entries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Shield size={32} className="mx-auto mb-2 opacity-30" />
            <p className="font-bold">No IPs whitelisted</p>
            <p className="text-sm">All IPs can currently access the admin panel.</p>
          </div>
        ) : (
          <div className="divide-y-2 divide-black">
            {entries.map(entry => (
              <div key={entry.id} className="flex items-center justify-between p-4 hover:bg-gray-50" data-testid={`whitelist-entry-${entry.ip}`}>
                <div>
                  <span className="font-mono font-bold text-sm">{entry.ip}</span>
                  {entry.label && <span className="ml-3 text-xs bg-[#FFD600] border border-black px-2 py-0.5 rounded-full font-bold">{entry.label}</span>}
                  <p className="text-xs text-gray-400 mt-0.5">Added {new Date(entry.created_at).toLocaleDateString()}</p>
                </div>
                <button onClick={() => removeIp(entry)} className="text-red-500 hover:text-red-700 p-2" data-testid={`whitelist-remove-${entry.ip}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Settings ─────────────────────────────────────────────────────────────────
function SiteSettings({ authHeaders }) {
  const [settings, setSettings] = useState({ stripe_publishable_key: "", stripe_secret_key: "", resend_api_key: "", from_email: "onboarding@resend.dev", notification_email: "hello@nofusswebsites.com.au", emergent_llm_key: "" });
  const [saving, setSaving] = useState(false);
  const [newAdminPw, setNewAdminPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, authHeaders()).then(r => setSettings(s => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, authHeaders());
      toast.success("Settings saved!");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const changeAdminPassword = async () => {
    if (newAdminPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newAdminPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    setChangingPw(true);
    try {
      await axios.put(`${API}/admin/change-password`, { password: newAdminPw }, authHeaders());
      toast.success("Admin password changed!");
      setNewAdminPw(""); setConfirmPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to change password"); } finally { setChangingPw(false); }
  };

  const fields = [
    { key: "emergent_llm_key", label: "AI Chatbot Key (Emergent LLM)", placeholder: "ek-...", help: "Powers the live chatbot. Get from Emergent → Profile → Universal Key", type: "password" },
    { key: "stripe_publishable_key", label: "Stripe Publishable Key", placeholder: "pk_live_...", help: "Get from stripe.com → Developers → API Keys" },
    { key: "stripe_secret_key", label: "Stripe Secret Key", placeholder: "sk_live_...", help: "Keep this secret! Never share publicly", type: "password" },
    { key: "resend_api_key", label: "Resend API Key", placeholder: "re_...", help: "Sign up at resend.com → API Keys → Create Key (free plan: 3,000 emails/month)", type: "password" },
    { key: "from_email", label: "From Email (Resend)", placeholder: "onboarding@resend.dev", help: "Use onboarding@resend.dev for testing, or verify your domain" },
    { key: "notification_email", label: "Notification Email", placeholder: "hello@nofusswebsites.com.au", help: "Where to send new lead & purchase notifications" },
  ];

  return (
    <div data-testid="admin-settings">
      <h2 className="text-3xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Settings</h2>
      <p className="text-gray-600 mb-8">Configure your integrations. These are stored securely in your database.</p>

      {/* Change Admin Password */}
      <div className="bg-white border-2 border-black rounded-2xl p-8 mb-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
        <h3 className="font-black text-lg mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          <Lock size={18} /> Change Owner Password
        </h3>
        <p className="text-sm text-gray-600 mb-4">This changes the owner login password. The change is saved to the database and works immediately — even after redeployment.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">New Password</label>
            <input type="password" value={newAdminPw} onChange={e => setNewAdminPw(e.target.value)} placeholder="Min 6 characters"
              className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm" data-testid="new-admin-pw" />
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">Confirm Password</label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Re-enter password"
              className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm" data-testid="confirm-admin-pw" />
          </div>
        </div>
        <button onClick={changeAdminPassword} disabled={changingPw || !newAdminPw} className="btn-primary flex items-center gap-2" data-testid="change-admin-pw-btn">
          <Save size={14} /> {changingPw ? "Changing..." : "Change Password"}
        </button>
      </div>

      <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-4 mb-8">
        <p className="font-black text-sm">After deploying, re-enter your API keys below if they're not showing. These are stored in the database and persist across deployments — once saved, you won't need to re-enter them.</p>
      </div>

      <div className="bg-white border-2 border-black rounded-2xl p-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
        <div className="space-y-5">
          {fields.map(f => (
            <div key={f.key}>
              <label className="text-xs font-black uppercase tracking-wider mb-1 block">{f.label}</label>
              <input
                type={f.type || "text"}
                value={settings[f.key] || ""}
                onChange={e => setSettings({ ...settings, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600] font-mono"
                data-testid={`setting-${f.key}`}
              />
              {f.help && <p className="text-xs text-gray-500 mt-1">{f.help}</p>}
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving} className="btn-primary mt-8 flex items-center gap-2" data-testid="save-settings-btn">
          <Save size={14} /> {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

// ─── Pages Editor ─────────────────────────────────────────────────────────────
function PagesEditor({ authHeaders }) {
  const PAGE_SLUGS = ["home", "about", "packages", "how-it-works", "contact"];
  const [selectedSlug, setSelectedSlug] = useState("home");
  const [page, setPage] = useState({ slug: "", title: "", content: "", meta_title: "", meta_description: "" });
  const [preview, setPreview] = useState(false);

  useEffect(() => { loadPage(selectedSlug); }, [selectedSlug]);

  const loadPage = async (slug) => {
    try {
      const r = await axios.get(`${API}/pages/${slug}`, authHeaders());
      setPage(r.data);
    } catch { setPage({ slug, title: slug, content: "", meta_title: "", meta_description: "" }); }
  };

  const save = async () => {
    try {
      await axios.put(`${API}/admin/pages/${selectedSlug}`, page, authHeaders());
      toast.success("Page saved!");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="admin-pages">
      <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>Page HTML Editor</h2>
      <div className="flex flex-wrap gap-3 mb-6">
        {PAGE_SLUGS.map(s => (
          <button key={s} onClick={() => setSelectedSlug(s)}
            className={`px-4 py-2 border-2 border-black rounded-xl text-sm font-bold capitalize transition-colors ${selectedSlug === s ? "bg-[#FFD600]" : "bg-white hover:bg-[#FFD600]"}`}
            data-testid={`page-tab-${s}`}>{s.replace("-", " ")}</button>
        ))}
      </div>

      <div className="bg-white border-2 border-black rounded-2xl p-6" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">Page Title</label>
            <input value={page.title || ""} onChange={e => setPage({ ...page, title: e.target.value })}
              className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm" data-testid="page-title-input" />
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">Meta Title (SEO)</label>
            <input value={page.meta_title || ""} onChange={e => setPage({ ...page, meta_title: e.target.value })}
              className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm" data-testid="page-meta-title-input" />
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-black uppercase tracking-wider mb-1 block">Meta Description (SEO)</label>
          <input value={page.meta_description || ""} onChange={e => setPage({ ...page, meta_description: e.target.value })}
            className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm" placeholder="160 characters max..." data-testid="page-meta-desc-input" />
        </div>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-black uppercase tracking-wider">Custom HTML Content</label>
            <button onClick={() => setPreview(!preview)} className="text-xs font-bold flex items-center gap-1 hover:text-[#0055FF]"><Eye size={12} /> {preview ? "Hide" : "Show"} Preview</button>
          </div>
          <textarea value={page.content || ""} onChange={e => setPage({ ...page, content: e.target.value })}
            rows={16} placeholder="<h2>Custom Section</h2><p>Add your HTML content here...</p>"
            className="w-full border-2 border-black rounded-xl px-3 py-2 text-sm font-mono resize-y"
            data-testid="page-content-textarea" />
        </div>
        {preview && page.content && (
          <div className="border-2 border-black rounded-xl p-4 bg-[#F4F4F5] mb-4">
            <p className="text-xs font-black uppercase tracking-wider mb-2">Preview:</p>
            <div className="blog-content" dangerouslySetInnerHTML={{ __html: page.content }} />
          </div>
        )}
        <button onClick={save} className="btn-primary flex items-center gap-2" data-testid="save-page-btn"><Save size={14} /> Save Page</button>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [section, setSection] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout, authHeaders } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const portfolioFields = [
    { key: "title", label: "Project Title", placeholder: "Mitchell's Cafe" },
    { key: "category", label: "Category", placeholder: "Restaurant / Tradie / Retail / Coach / Health & Wellness" },
    { key: "description", label: "Description", placeholder: "Brief description", type: "textarea", rows: 2 },
    { key: "image_url", label: "Image URL", placeholder: "https://..." },
    { key: "client_url", label: "Website URL (optional)", placeholder: "https://client.com.au" },
  ];

  const testimonialsFields = [
    { key: "client_name", label: "Client Name", placeholder: "Jane Smith" },
    { key: "business_name", label: "Business Name", placeholder: "Jane's Cafe, Richmond" },
    { key: "review", label: "Review", placeholder: "Amazing service...", type: "textarea", rows: 3, full: true },
    { key: "rating", label: "Rating (1-5)", placeholder: "5", type: "number" },
    { key: "package_used", label: "Package Used", placeholder: "Starter / Business / eCommerce" },
    { key: "avatar_url", label: "Avatar Image URL (optional)", placeholder: "https://..." },
  ];

  const blogFields = [
    { key: "title", label: "Post Title", placeholder: "5 Tips for Small Business Websites", full: true },
    { key: "slug", label: "URL Slug", placeholder: "5-tips-for-small-business-websites", full: true },
    { key: "category", label: "Category", placeholder: "Web Design Tips / SEO & Performance / Business Tips" },
    { key: "excerpt", label: "Excerpt", placeholder: "Short summary...", type: "textarea", rows: 2, full: true },
    { key: "content", label: "Content (HTML)", placeholder: "<h2>Title</h2><p>Content...</p>", type: "textarea", rows: 14, full: true },
    { key: "image_url", label: "Featured Image URL", placeholder: "https://..." },
    { key: "is_published", label: "Published", type: "checkbox" },
  ];

  const serviceFields = [
    { key: "title", label: "Service Title", placeholder: "Local SEO Services", full: true },
    { key: "slug", label: "URL Slug", placeholder: "local-seo-services", full: true },
    { key: "excerpt", label: "Short Description", placeholder: "Brief overview...", type: "textarea", rows: 2, full: true },
    { key: "content", label: "Full Content (HTML)", placeholder: "<h2>About this service</h2>", type: "textarea", rows: 12, full: true },
    { key: "icon", label: "Icon Name (lucide)", placeholder: "Search / Globe / Zap" },
    { key: "is_published", label: "Published", type: "checkbox" },
  ];

  const renderSection = () => {
    switch (section) {
      case "overview": return <Overview authHeaders={authHeaders} />;
      case "contacts": return <Contacts authHeaders={authHeaders} />;
      case "chats": return <LiveChats authHeaders={authHeaders} />;
      case "analytics": return <Analytics authHeaders={authHeaders} />;
      case "purchases": return <Purchases authHeaders={authHeaders} />;
      case "packages": return <Packages authHeaders={authHeaders} />;
      case "portfolio": return <CRUDSection key="portfolio" title="Portfolio" endpoint={`${API}/admin/portfolio`} fields={portfolioFields} authHeaders={authHeaders} testId="admin-portfolio" />;
      case "testimonials": return <CRUDSection key="testimonials" title="Testimonials" endpoint={`${API}/admin/testimonials`} fields={testimonialsFields} authHeaders={authHeaders} testId="admin-testimonials" />;
      case "blog": return <CRUDSection key="blog" title="Blog Posts" endpoint={`${API}/admin/blog`} fields={blogFields} authHeaders={authHeaders} testId="admin-blog" />;
      case "pages": return <PagesEditor authHeaders={authHeaders} />;
      case "services": return <CRUDSection key="services" title="Service Pages" endpoint={`${API}/admin/services`} fields={serviceFields} authHeaders={authHeaders} testId="admin-services" />;
      case "tracking": return <TrackingCodes authHeaders={authHeaders} />;
      case "users": return <UserManagement authHeaders={authHeaders} />;
      case "whitelist": return <IPWhitelist authHeaders={authHeaders} />;
      case "settings": return <SiteSettings authHeaders={authHeaders} />;
      default: return <Overview authHeaders={authHeaders} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-black text-white flex flex-col border-r-4 border-[#FFD600] transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:static md:h-screen`}>
        {/* Logo */}
        <div className="p-6 border-b-2 border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFD600] border-2 border-white rounded-xl flex items-center justify-center">
              <Zap size={18} fill="black" className="text-black" />
            </div>
            <div>
              <p className="font-black text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>No Fuss Websites</p>
              <p className="text-xs text-[#FFD600] font-bold">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => { setSection(s.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all ${section === s.id ? "bg-[#FFD600] text-black" : "hover:bg-white/10 text-white"}`}
              data-testid={`sidebar-${s.id}`}
            >
              <s.icon size={16} />
              {s.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t-2 border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm hover:bg-red-900/50 transition-colors text-red-300" data-testid="logout-btn">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b-2 border-black px-6 h-16 flex items-center justify-between sticky top-0 z-40">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="md:hidden p-2 border-2 border-black rounded-lg hover:bg-[#FFD600]">
            <LayoutDashboard size={18} />
          </button>
          <h1 className="font-black text-lg hidden md:block" style={{ fontFamily: "Outfit, sans-serif" }}>
            {SECTIONS.find(s => s.id === section)?.label || "Dashboard"}
          </h1>
          <div className="flex items-center gap-3 text-sm">
            <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 font-bold text-[#0055FF] hover:underline">
              <Eye size={14} /> View Site
            </a>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 md:p-8 overflow-auto">
          {renderSection()}
        </main>
      </div>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
}
